import { useEffect, useState } from 'react'
import { useAppState } from '@/app/providers/AppStateProvider'
import { useAuth } from '@/features/auth'
import { openConsentPreferences } from '@/shared/consent'
import { BillingSection } from './BillingSection'
import { MembersSection } from './MembersSection'
import { PrivacyDataSection } from './PrivacyDataSection'
import { WorkspaceSection } from './WorkspaceSection'

type Tab = 'workspace' | 'members' | 'account' | 'billing'

/**
 * Settings dialog. STEP 9.0 turned the placeholder into a real panel with
 * three tabs:
 *   - Workspace: vertical template, language, timezone.
 *   - Membros:   invite + role management.
 *   - Conta:     identity + logout.
 *
 * Owner/admin see write affordances; member/viewer/auditor see read-only
 * snapshots, but the dialog still opens for everyone.
 */
export function SettingsPlaceholder() {
  const { closeSettings } = useAppState()
  const { user, organization, role, logout } = useAuth()
  const [loggingOut, setLoggingOut] = useState(false)
  const [tab, setTab] = useState<Tab>('workspace')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSettings()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeSettings])

  const onLogout = async () => {
    if (loggingOut) return
    setLoggingOut(true)
    try {
      await logout()
    } finally {
      setLoggingOut(false)
      closeSettings()
    }
  }

  return (
    <div
      className="settings-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      onClick={closeSettings}
    >
      <div
        className="settings-modal settings-modal-wide"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="settings-modal-head">
          <h2 id="settings-title">Configurações</h2>
          <button
            type="button"
            className="settings-button"
            onClick={closeSettings}
            aria-label="Fechar configurações"
          >
            Fechar
          </button>
        </header>

        <nav className="settings-tabs" role="tablist" aria-label="Seções">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'workspace'}
            className={`settings-tab${tab === 'workspace' ? ' is-active' : ''}`}
            onClick={() => setTab('workspace')}
          >
            Workspace
          </button>
          <button
            type="button"
            role="tab"
            data-testid="settings-tab-members"
            aria-selected={tab === 'members'}
            className={`settings-tab${tab === 'members' ? ' is-active' : ''}`}
            onClick={() => setTab('members')}
          >
            Membros
          </button>
          <button
            type="button"
            role="tab"
            data-testid="settings-tab-account"
            aria-selected={tab === 'account'}
            className={`settings-tab${tab === 'account' ? ' is-active' : ''}`}
            onClick={() => setTab('account')}
          >
            Conta
          </button>
          <button
            type="button"
            role="tab"
            data-testid="settings-tab-billing"
            aria-selected={tab === 'billing'}
            className={`settings-tab${tab === 'billing' ? ' is-active' : ''}`}
            onClick={() => setTab('billing')}
          >
            Plano
          </button>
        </nav>

        <div className="settings-modal-body">
          {tab === 'workspace' ? (
            <WorkspaceSection currentRole={role} />
          ) : null}
          {tab === 'members' ? (
            <MembersSection currentRole={role} />
          ) : null}
          {tab === 'account' ? (
            <>
            <div className="settings-section">
              <header className="settings-section-head">
                <h3>Sua conta</h3>
                <p className="settings-section-help">
                  Encerre a sessão para limpar os cookies HttpOnly. Os tokens
                  vivem apenas no servidor — nada é armazenado no browser.
                </p>
              </header>
              {user && (
                <div className="settings-card">
                  <div>
                    <strong>{user.full_name || user.email}</strong>
                  </div>
                  <div className="settings-meta">{user.email}</div>
                  {organization && (
                    <div className="settings-meta">
                      {organization.display_name} · {role ?? 'member'}
                    </div>
                  )}
                </div>
              )}
              <div className="settings-form-actions">
                {/* LGPD Block B — authenticated users never see the login
                    footer, so the consent dialog reopens from here too. The
                    consent overlay (z-90) renders above this modal. */}
                <button
                  type="button"
                  className="settings-button"
                  data-testid="settings-consent-preferences"
                  onClick={openConsentPreferences}
                >
                  Preferências de privacidade
                </button>
                <button
                  type="button"
                  className="settings-button is-danger"
                  data-testid="logout-button"
                  onClick={() => void onLogout()}
                  disabled={loggingOut}
                >
                  {loggingOut ? 'Saindo…' : 'Sair'}
                </button>
              </div>
            </div>
            <PrivacyDataSection />
            </>
          ) : null}
          {tab === 'billing' ? <BillingSection /> : null}
        </div>
      </div>
    </div>
  )
}
